export type TextContrastShade = 'light' | 'dark';

// The textbook-correct midpoint (L*=50) for deciding readable text shade via
// CIELAB L* assumes a symmetric black/white choice. Consumers that pick a
// non-black "dark" text color should calibrate their own threshold instead
// of relying on this default — see getReadableTextShade's threshold param.
export const DEFAULT_TEXT_CONTRAST_LIGHTNESS_THRESHOLD = 50;

const HEX_BODY_PATTERN = /^[0-9a-f]+$/;

/**
 * Normalizes a hex color string to lowercase '#rrggbb' — accepts '#rgb', '#rgba',
 * '#rrggbb', '#rrggbbaa' (case-insensitive, optional leading '#', surrounding
 * whitespace trimmed). Any alpha channel present is dropped — it is not
 * composited against an unknown background. Throws on anything else.
 */
export function normalizeHex(hexColor: string): string {
  const stripped = hexColor.trim().replace(/^#/, '').toLowerCase();

  if (!HEX_BODY_PATTERN.test(stripped)) {
    throw new Error(`Invalid hex color: "${hexColor}"`);
  }

  switch (stripped.length) {
    case 3: // #rgb
      return `#${expandShorthand(stripped)}`;
    case 4: // #rgba — alpha ignored
      return `#${expandShorthand(stripped.slice(0, 3))}`;
    case 6: // #rrggbb
      return `#${stripped}`;
    case 8: // #rrggbbaa — alpha ignored
      return `#${stripped.slice(0, 6)}`;
    default:
      throw new Error(`Invalid hex color: "${hexColor}"`);
  }
}

function expandShorthand(threeDigits: string): string {
  return threeDigits
    .split('')
    .map(digit => digit + digit)
    .join('');
}

/**
 * Computes which text shade ('light' | 'dark') gives adequate contrast against
 * normalizedHex, via CIELAB L* (perceptual lightness) compared against
 * lightnessThreshold. Pure, no cache — see get-readable-text-shade.ts for the
 * memoized public entry point.
 */
export function computeReadableTextShade(
  normalizedHex: string,
  lightnessThreshold: number = DEFAULT_TEXT_CONTRAST_LIGHTNESS_THRESHOLD,
): TextContrastShade {
  const { r, g, b } = parseHex(normalizedHex);
  const luminance = toRelativeLuminance(linearizeChannel(r), linearizeChannel(g), linearizeChannel(b));
  const lightness = toLabLightness(luminance);

  return lightness > lightnessThreshold ? 'dark' : 'light';
}

function parseHex(normalizedHex: string): { r: number; g: number; b: number } {
  const body = normalizedHex.slice(1);
  return {
    r: parseInt(body.slice(0, 2), 16) / 255,
    g: parseInt(body.slice(2, 4), 16) / 255,
    b: parseInt(body.slice(4, 6), 16) / 255,
  };
}

// sRGB EOTF (gamma decoding) — https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
function linearizeChannel(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

// D65 white point, CIE standard luminosity weights.
function toRelativeLuminance(r: number, g: number, b: number): number {
  return 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
}

// CIE L* (perceptual lightness), cube-root compression of Y.
function toLabLightness(luminance: number): number {
  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;
  const f = luminance > epsilon ? Math.cbrt(luminance) : (kappa * luminance + 16) / 116;
  return 116 * f - 16;
}
